import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { briefsListUrl, suppliersListUrl, supplierOpStatus } from '../lib/reportingDrill';
import { fmtDate } from '../lib/dates';
import { feedbackState, feedbackOutcomeLabel } from '../lib/briefFeedback';
import { CARD_SHELL_SM, SectionHeader, RefreshControl } from './reporting/MetricCard';

// SuppliersReporting — the Reporting tab on the Suppliers page.
//
// Two top-level sections, mirroring the spec's audience split:
//
//   At a Glance — operational read on the supplier base. Status tiles
//                 ("ready to brief", "blocked"), backlog detector,
//                 engagement signals, top responders + dormant detail.
//                 For team leads.
//
//   Trends & Insights — strategic read. Spend totals + weekly trend,
//                       win rate, awarded spend by supplier, tier
//                       composition, category + region distribution,
//                       most-engaged leaderboard. For directors.
//
// Every clickable number / bar / row routes to a filtered List view
// or supplier detail via the canonical URL contract in lib/reportingDrill.
// Drill destinations carry ?ref=reporting so the destination renders
// the Back-to-Reporting affordance.
//
// Props:
//   suppliers — array of suppliers with aggregates already attached
//               (response_count, last_activity_at, awarded_count,
//               awarded_total) from Suppliers.js's fetch. We don't
//               re-fetch these.
//
// Side fetches inside the component:
//   - brief_recipients + brief_responses with timestamps (for 90-day
//     invited/responded counts + new-suppliers delta)
//   - briefs with closed_at + awarded_amount (for weekly spend chart)
//   These are small queries; component-local rather than threading
//   through Suppliers.js's main fetch.

const TIMEFRAMES = [
  { value: '7d',  label: '7D'  },
  { value: '30d', label: '30D' },
  { value: '90d', label: '90D' },
  { value: 'ytd', label: 'YTD' },
  { value: 'all', label: 'All' },
];
const TIMEFRAME_DAYS = { '7d': 7, '30d': 30, '90d': 90, '365d': 365 };
// The Reporting page header speaks a canonical uppercase timeframe; map it to
// this tab's lowercase vocabulary at the prop boundary.
const CANON_TO_SUP = { '7D': '7d', '30D': '30d', '90D': '90d', 'YTD': 'ytd', 'All': 'all' };

const fmtGbp     = (n) => `£${Math.round(n || 0).toLocaleString()}`;
const fmtGbpFull = (n) => `£${(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtRelative = (iso) => {
  if (!iso) return '—';
  const diffMs = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const day = Math.floor(hr / 24);
  if (day < 14) return `${day} day${day === 1 ? '' : 's'} ago`;
  return fmtDate(iso);
};

// Walk back N weeks from today, producing { start, end, label } cells
// in chronological order (oldest → newest). The bar chart renders in
// this order so the most-recent week is on the right.
const buildWeekBuckets = (count = 12) => {
  const buckets = [];
  const now = new Date();
  for (let i = count - 1; i >= 0; i--) {
    const end = new Date(now);
    end.setDate(end.getDate() - i * 7);
    const start = new Date(end);
    start.setDate(start.getDate() - 6);
    buckets.push({
      start, end,
      label: `${start.getDate()}/${start.getMonth() + 1}`,
      key: `${start.getFullYear()}-W${Math.floor((start - new Date(start.getFullYear(), 0, 1)) / 86400000 / 7)}`,
    });
  }
  return buckets;
};

// Compute the start date for a given timeframe value. 'all' returns null.
const timeframeStartDate = (tf) => {
  if (!tf || tf === 'all') return null;
  if (tf === 'ytd') {
    const d = new Date();
    return new Date(d.getFullYear(), 0, 1);
  }
  const days = TIMEFRAME_DAYS[tf];
  if (!days) return null;
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d;
};

export default function SuppliersReporting({ suppliers, timeframe: timeframeProp = '90D' }) {
  const navigate = useNavigate();
  // The page header owns the timeframe; map it to this tab's vocabulary.
  const globalTf = CANON_TO_SUP[timeframeProp] || '90d';
  const [refreshKey, setRefreshKey] = useState(0);
  const [updatedAt, setUpdatedAt] = useState(new Date());

  // Dormant + under-utilised are DEFINITIONS, not timeframes (reporting-aligned
  // §4): "no activity in 90+ days" holds whatever period you're reporting on, so
  // the threshold is fixed and stated in the card text rather than a dropdown.
  const dormantTimeframe = '90d';
  const underUtilisedTimeframe = '90d';
  // Spend / awarded-value are genuine timeframe filters. They DEFAULT to the
  // header timeframe but stay individually overridable (Trends cards); moving
  // the header re-syncs them to the new value.
  const [spendTimeframe,         setSpendTimeframe]         = useState(globalTf);
  const [awardedBySupplierTf,    setAwardedBySupplierTf]    = useState(globalTf);
  const [topRespondingSort,      setTopRespondingSort]      = useState('response_count');
  useEffect(() => {
    setSpendTimeframe(globalTf);
    setAwardedBySupplierTf(globalTf);
  }, [globalTf]);

  // Side-fetched data — kept local so Suppliers.js doesn't need to
  // thread it through. Reload on refresh-icon click.
  const [recipients, setRecipients] = useState([]);
  const [responses,  setResponses]  = useState([]);
  const [awardedBriefs, setAwardedBriefs] = useState([]);
  // Post-event feedback — the feedback rows, open incidents, and the value
  // threshold that decides which awards are asked for feedback at all.
  const [feedbackRows, setFeedbackRows] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [feedbackThreshold, setFeedbackThreshold] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [recRes, resRes, briefRes, fbRes, incRes, setRes] = await Promise.all([
        supabase.from('brief_recipients').select('supplier_id, notified_at, status, accepted_at, responded_at, declined_at'),
        supabase.from('brief_responses').select('supplier_id, submitted_at, is_submitted'),
        supabase.from('briefs').select('id, brief_name, awarded_supplier_id, awarded_amount, closed_at, status, event_live_date, event_live_end_date, owner_id, owner:internal_users!owner_id(id, full_name)').eq('status', 'closed').not('awarded_supplier_id', 'is', null),
        supabase.from('brief_feedback').select('brief_id, supplier_id, status, completed_at, delivered, on_time, safety, use_again, note'),
        supabase.from('supplier_incidents').select('supplier_id, severity, closed_at'),
        supabase.from('app_settings').select('value').eq('key', 'feedback_value_threshold_gbp').maybeSingle(),
      ]);
      if (cancelled) return;
      setRecipients(recRes.data || []);
      setResponses(resRes.data || []);
      setAwardedBriefs(briefRes.data || []);
      setFeedbackRows(fbRes.data || []);
      setIncidents(incRes.data || []);
      setFeedbackThreshold(Math.max(0, Number(setRes.data?.value) || 0));
      setUpdatedAt(new Date());
    })();
    return () => { cancelled = true; };
  }, [refreshKey]);

  // ====================================================================
  // DERIVED VIEW DATA
  // ====================================================================

  // Suppliers split by synthesised operational status.
  const opBuckets = useMemo(() => {
    const out = { active: [], preferred: [], on_hold: [], do_not_use: [], pending_setup: [] };
    for (const s of suppliers || []) {
      const k = supplierOpStatus(s);
      if (k && out[k]) out[k].push(s);
    }
    return out;
  }, [suppliers]);

  const total = (suppliers || []).length;
  const addedLast30d = useMemo(() => {
    const cutoff = Date.now() - 30 * 86400000;
    return (suppliers || []).filter(s => s.created_at && new Date(s.created_at).getTime() >= cutoff).length;
  }, [suppliers]);
  const onboarded = useMemo(() => (suppliers || []).filter(s => s.finance_approved === true).length, [suppliers]);
  const archivedThisYear = useMemo(() => {
    const yearStart = new Date(new Date().getFullYear(), 0, 1).getTime();
    return (suppliers || []).filter(s => s.archived_at && new Date(s.archived_at).getTime() >= yearStart).length;
  }, [suppliers]);

  // Backlog detector — surfaces a friendly nudge when more than 20% of
  // the supplier base is still pending Fusion-side account setup.
  const pendingSetupPercent = total > 0 ? (opBuckets.pending_setup.length / total) * 100 : 0;
  const showBacklog = pendingSetupPercent > 20;

  // 90-day invite + response activity per supplier — drives dormant +
  // under-utilised counts. last_activity_at on the supplier row is the
  // primary signal; recipients/responses give us "ever-invited".
  const supplierActivity = useMemo(() => {
    const m = new Map();
    for (const r of recipients) {
      if (!r.supplier_id) continue;
      const entry = m.get(r.supplier_id) || { invited: 0, invited90: 0, responded: 0, responded90: 0, won: 0 };
      entry.invited += 1;
      if (r.notified_at && Date.now() - new Date(r.notified_at).getTime() <= 90 * 86400000) entry.invited90 += 1;
      m.set(r.supplier_id, entry);
    }
    for (const r of responses) {
      if (!r.is_submitted) continue;
      const entry = m.get(r.supplier_id) || { invited: 0, invited90: 0, responded: 0, responded90: 0, won: 0 };
      entry.responded += 1;
      const at = r.submitted_at;
      if (at && Date.now() - new Date(at).getTime() <= 90 * 86400000) entry.responded90 += 1;
      m.set(r.supplier_id, entry);
    }
    for (const b of awardedBriefs) {
      if (!b.awarded_supplier_id) continue;
      const entry = m.get(b.awarded_supplier_id) || { invited: 0, invited90: 0, responded: 0, responded90: 0, won: 0 };
      entry.won += 1;
      m.set(b.awarded_supplier_id, entry);
    }
    return m;
  }, [recipients, responses, awardedBriefs]);

  // Dormant: active, not blocked, no brief activity in {dormantTimeframe}.
  const dormantList = useMemo(() => {
    const cutoffDays = TIMEFRAME_DAYS[dormantTimeframe] || 90;
    const cutoff = Date.now() - cutoffDays * 86400000;
    return (suppliers || []).filter(s =>
      s.status === 'active'
      && !s.on_hold
      && s.do_not_use_status !== 'Do Not Use'
      && (!s.last_activity_at || new Date(s.last_activity_at).getTime() < cutoff)
    ).sort((a, b) => {
      const aT = a.last_activity_at ? new Date(a.last_activity_at).getTime() : 0;
      const bT = b.last_activity_at ? new Date(b.last_activity_at).getTime() : 0;
      return aT - bT;
    });
  }, [suppliers, dormantTimeframe]);

  // Under-utilised: active suppliers who haven't been invited to a brief
  // in the under-utilised window (default 90d). Differs from dormant in
  // that we care specifically about brief invites, not any activity.
  const underUtilisedCount = useMemo(() => {
    const days = TIMEFRAME_DAYS[underUtilisedTimeframe] || 90;
    const cutoff = Date.now() - days * 86400000;
    let count = 0;
    for (const s of suppliers || []) {
      if (s.status !== 'active') continue;
      const activity = supplierActivity.get(s.id);
      // Filter recipients to ones in the window for this supplier.
      const inviteCount = (recipients || []).filter(r =>
        r.supplier_id === s.id && r.notified_at && new Date(r.notified_at).getTime() >= cutoff
      ).length;
      if (inviteCount === 0 && (!activity || activity.invited === 0)) count += 1;
      else if (inviteCount === 0) count += 1;
    }
    return count;
  }, [suppliers, recipients, supplierActivity, underUtilisedTimeframe]);

  // New suppliers in last 30 days + delta vs the previous 30 days.
  const newSuppliers30d = useMemo(() => {
    const now = Date.now();
    const cutoff = now - 30 * 86400000;
    const prevCutoff = now - 60 * 86400000;
    let current = 0;
    let previous = 0;
    for (const s of suppliers || []) {
      if (!s.created_at) continue;
      const t = new Date(s.created_at).getTime();
      if (t >= cutoff) current += 1;
      else if (t >= prevCutoff) previous += 1;
    }
    return { count: current, delta: current - previous };
  }, [suppliers]);

  // Top responders — sort by chosen metric. Min 3 responses to be
  // ranked (avoids one-shot wonders dominating the list).
  const topResponding = useMemo(() => {
    const ranked = (suppliers || [])
      .map(s => ({ ...s, ...(supplierActivity.get(s.id) || {}) }))
      .filter(s => (s.responded || 0) >= 1);
    const sortField = topRespondingSort;
    return ranked.sort((a, b) => {
      if (sortField === 'win_rate') {
        const ar = a.responded > 0 ? (a.won || 0) / a.responded : 0;
        const br = b.responded > 0 ? (b.won || 0) / b.responded : 0;
        return br - ar;
      }
      return (b.responded || 0) - (a.responded || 0);
    }).slice(0, 8);
  }, [suppliers, supplierActivity, topRespondingSort]);

  // Total awarded value over the spend timeframe + weekly breakdown
  // (always last 12 weeks regardless of timeframe — the bar chart
  // doesn't scale to longer windows usefully).
  const weeklySpend = useMemo(() => {
    const buckets = buildWeekBuckets(12);
    for (const b of awardedBriefs) {
      if (!b.closed_at || !b.awarded_amount) continue;
      const ts = new Date(b.closed_at).getTime();
      for (const w of buckets) {
        if (ts >= w.start.getTime() && ts <= w.end.getTime() + 86400000) {
          w.total = (w.total || 0) + Number(b.awarded_amount);
          break;
        }
      }
    }
    return buckets;
  }, [awardedBriefs]);
  const weeklySpendMax = Math.max(1, ...weeklySpend.map(w => w.total || 0));
  const totalAwardedInTimeframe = useMemo(() => {
    const start = timeframeStartDate(spendTimeframe);
    return awardedBriefs.reduce((sum, b) => {
      if (!b.awarded_amount) return sum;
      if (start && (!b.closed_at || new Date(b.closed_at) < start)) return sum;
      return sum + Number(b.awarded_amount);
    }, 0);
  }, [awardedBriefs, spendTimeframe]);

  // Avg Win Rate — only count suppliers with ≥3 responses. Empty when
  // sample is too small to be meaningful.
  const avgWinRate = useMemo(() => {
    const eligible = [];
    for (const s of suppliers || []) {
      const act = supplierActivity.get(s.id);
      if (!act || act.responded < 3) continue;
      eligible.push(act.won / act.responded);
    }
    if (eligible.length === 0) return null;
    return eligible.reduce((a, b) => a + b, 0) / eligible.length;
  }, [suppliers, supplierActivity]);

  // Awarded value by supplier — top 5 over the awardedBySupplier timeframe.
  const awardedBySupplier = useMemo(() => {
    const start = timeframeStartDate(awardedBySupplierTf);
    const m = new Map(); // supplier_id → { amount, count }
    for (const b of awardedBriefs) {
      if (!b.awarded_supplier_id) continue;
      if (start && (!b.closed_at || new Date(b.closed_at) < start)) continue;
      const entry = m.get(b.awarded_supplier_id) || { amount: 0, count: 0 };
      entry.amount += Number(b.awarded_amount) || 0;
      entry.count += 1;
      m.set(b.awarded_supplier_id, entry);
    }
    return (suppliers || [])
      .map(s => ({ ...s, ...(m.get(s.id) || { amount: 0, count: 0 }) }))
      .filter(s => s.amount > 0)
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 5);
  }, [suppliers, awardedBriefs, awardedBySupplierTf]);
  const awardedBySupplierMax = Math.max(1, ...awardedBySupplier.map(s => s.amount));

  // Tier composition: tier 1 / tier 2 / unassigned. The brief specifies
  // only 1 + 2 — anything else (legacy null) shows in "Unassigned".
  const tierComposition = useMemo(() => {
    const counts = { 1: 0, 2: 0, unassigned: 0 };
    for (const s of suppliers || []) {
      if (s.tier === 1) counts[1] += 1;
      else if (s.tier === 2) counts[2] += 1;
      else counts.unassigned += 1;
    }
    return counts;
  }, [suppliers]);
  const tierUnbalanced = tierComposition[1] > 0 && tierComposition[2] / Math.max(1, tierComposition[1]) > 8;

  // Category distribution — top 7. Excludes uncategorised rows.
  const byCategory = useMemo(() => {
    const m = new Map();
    for (const s of suppliers || []) {
      const c = (s.category || '').trim();
      if (!c) continue;
      m.set(c, (m.get(c) || 0) + 1);
    }
    return Array.from(m.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([name, count]) => ({ name, count, pct: total > 0 ? (count / total) * 100 : 0 }));
  }, [suppliers, total]);
  const topCategories = byCategory.slice(0, 7);
  const categoryMax = Math.max(1, ...topCategories.map(c => c.count));
  const totalCategoryCount = byCategory.length;

  // Region distribution. Top region gets the green accent; concentration
  // > 40% triggers an inline diversity insight pill.
  const byRegion = useMemo(() => {
    const m = new Map();
    for (const s of suppliers || []) {
      const r = (s.region || '').trim();
      if (!r) continue;
      m.set(r, (m.get(r) || 0) + 1);
    }
    return Array.from(m.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([name, count]) => ({ name, count, pct: total > 0 ? (count / total) * 100 : 0 }));
  }, [suppliers, total]);
  const topRegion = byRegion[0];
  const regionMax = Math.max(1, ...byRegion.map(r => r.count));
  const regionConcentration = topRegion ? topRegion.pct : 0;
  const regionsConcentrated = regionConcentration > 40;

  // Most engaged leaderboard — top 10 by response rate (responded /
  // invited), min 3 invites. Mirrors the brief's "engagement table" card.
  const mostEngaged = useMemo(() => {
    return (suppliers || [])
      .map(s => {
        const act = supplierActivity.get(s.id) || { invited: 0, responded: 0 };
        const rate = act.invited >= 3 ? act.responded / act.invited : null;
        return { ...s, invited: act.invited, responded: act.responded, rate };
      })
      .filter(s => s.rate != null)
      .sort((a, b) => b.rate - a.rate)
      .slice(0, 10);
  }, [suppliers, supplierActivity]);

  // ====================================================================
  // POST-EVENT FEEDBACK
  // ====================================================================
  // Feedback is one row per (brief, awarded supplier); an award is single,
  // so brief_id keys it. We walk every awarded brief through feedbackState
  // (same helper the brief page uses) so the completion rate here can never
  // drift from what owners actually see. "Reached" = feedback is due, given
  // or lapsed — i.e. past the point it should exist. Completion rate is
  // computed FIRST because everything below is a read of the answers we do
  // have, and that number says how much of the picture is filled in.
  const supplierNameById = useMemo(() => {
    const m = new Map();
    for (const s of suppliers || []) m.set(s.id, s.supplier_name);
    return m;
  }, [suppliers]);

  const feedback = useMemo(() => {
    const byBrief = new Map();
    for (const f of feedbackRows) byBrief.set(f.brief_id, f);
    const now = new Date();

    let reached = 0, complete = 0;
    const completedRows = [];       // { brief, supplier name, feedback }
    const byOwner = new Map();      // owner_id → { name, reached, complete }

    for (const b of awardedBriefs) {
      const f = byBrief.get(b.id) || null;
      const state = feedbackState(b, f, { thresholdGBP: feedbackThreshold, now });
      if (state === 'n/a' || state === 'not_due') continue;
      reached += 1;
      const ownerKey = b.owner_id || 'unassigned';
      const owner = byOwner.get(ownerKey) || { name: b.owner?.full_name || 'Unassigned', reached: 0, complete: 0 };
      owner.reached += 1;
      if (state === 'complete') {
        complete += 1;
        owner.complete += 1;
        completedRows.push({ brief: b, supplierName: supplierNameById.get(b.awarded_supplier_id) || 'Unknown supplier', feedback: f });
      }
      byOwner.set(ownerKey, owner);
    }

    // Base performance — reads only from completed answers.
    let onTimeYes = 0, onTimeOf = 0, useAgainYes = 0, useAgainOf = 0;
    for (const { feedback: f } of completedRows) {
      if (f?.on_time) { onTimeOf += 1; if (f.on_time === 'yes') onTimeYes += 1; }
      if (f?.use_again) { useAgainOf += 1; if (f.use_again === 'yes') useAgainYes += 1; }
    }

    // Open incidents split by severity (own lifecycle, not tied to the window).
    let incMinor = 0, incReportable = 0;
    for (const i of incidents) {
      if (i.closed_at) continue;
      if (i.severity === 'reportable') incReportable += 1; else incMinor += 1;
    }

    // Watchlist — completed feedback whose outcome wasn't clean, most recent
    // first. Capped in the render; the count drives the header.
    const watchlist = completedRows
      .map(r => ({ ...r, outcome: feedbackOutcomeLabel('complete', r.feedback) }))
      .filter(r => r.outcome && (r.outcome.tone === 'bad' || r.outcome.tone === 'warn'))
      .sort((a, b) => new Date(b.feedback?.completed_at || 0) - new Date(a.feedback?.completed_at || 0));

    const owners = Array.from(byOwner.values())
      .sort((a, b) => (b.reached - b.complete) - (a.reached - a.complete) || a.name.localeCompare(b.name));

    return {
      reached, complete,
      rate: reached > 0 ? complete / reached : null,
      outstanding: reached - complete,
      onTime: onTimeOf > 0 ? { pct: onTimeYes / onTimeOf, of: onTimeOf } : null,
      useAgain: useAgainOf > 0 ? { pct: useAgainYes / useAgainOf, of: useAgainOf } : null,
      incMinor, incReportable, incOpen: incMinor + incReportable,
      watchlist,
      owners,
    };
  }, [awardedBriefs, feedbackRows, incidents, feedbackThreshold, supplierNameById]);

  // ====================================================================
  // DRILL HELPERS — every clickable element routes through these
  // ====================================================================

  // Rich banner descriptors per drill type — these flow through the
  // URL via banner_descriptor + banner_tone params so the destination
  // list shows the same copy after a refresh as it does on the
  // initial drill click.
  const OP_STATUS_BANNER = {
    active:        { descriptor: 'currently Active',                 tone: 'green' },
    preferred:     { descriptor: 'flagged as Preferred',             tone: 'green' },
    on_hold:       { descriptor: 'currently On Hold',                tone: 'amber' },
    do_not_use:    { descriptor: 'flagged Do Not Use',               tone: 'red' },
    pending_setup: { descriptor: 'awaiting Fusion account set-up',   tone: 'teal' },
  };
  const drillOpStatus = (op) => navigate(suppliersListUrl({
    opStatus: op,
    bannerDescriptor: OP_STATUS_BANNER[op]?.descriptor,
    bannerTone:       OP_STATUS_BANNER[op]?.tone,
  }));
  const drillTier     = (t)  => navigate(suppliersListUrl({
    tier: t,
    bannerDescriptor: `flagged as Tier ${t}`,
    bannerTone: 'teal',
  }));
  const drillCategory = (c)  => navigate(suppliersListUrl({
    category: c,
    bannerDescriptor: `in the ${c} category`,
    bannerTone: 'teal',
  }));
  const drillRegion   = (r)  => navigate(suppliersListUrl({
    region: r,
    bannerDescriptor: `in ${r}`,
    bannerTone: 'teal',
  }));
  const drillDormant  = (tf) => navigate(suppliersListUrl({
    dormant: tf,
    bannerDescriptor: `with no activity in the past ${tf === '180d' ? '180' : '90'} days`,
    bannerTone: 'amber',
  }));
  const drillNewlyCreated = (window) => navigate(suppliersListUrl({
    createdWithin: window,
    bannerDescriptor: `added in the last ${window === '30d' ? '30' : window === '90d' ? '90' : '365'} days`,
    bannerTone: 'teal',
  }));
  const drillUnderUtilised = () => navigate(suppliersListUrl({
    opStatus: 'active',
    briefsInvitedIn90d: 0,
    bannerDescriptor: 'active but never invited to a brief in 90 days',
    bannerTone: 'amber',
  }));
  const drillSupplier = (id) => navigate(`/suppliers/${id}`);
  const drillAllSuppliers = () => navigate('/suppliers');
  const drillAwardedTotal = () => navigate(briefsListUrl({
    status: 'closed', awardState: 'awarded', sort: 'amount_desc',
    bannerDescriptor: 'awarded — ranked by amount',
    bannerTone: 'green',
  }));
  const drillAwardedToSupplier = (id) => navigate(briefsListUrl({
    awardedTo: id, awardState: 'awarded',
    bannerDescriptor: 'awarded to this supplier',
    bannerTone: 'green',
  }));

  // ====================================================================
  // RENDER
  // ====================================================================

  return (
    <div className="font-['Metropolis']">

      {/* ===== TOOLBAR ===== (timeframe lives in the page header now) */}
      <div className="flex items-center justify-end gap-4 mb-6 flex-wrap">
        <RefreshControl updatedLabel={`Updated ${fmtRelative(updatedAt.toISOString())}`} onRefresh={() => setRefreshKey(k => k + 1)} />
      </div>

      {/* ===== SECTION 1: AT A GLANCE ===== */}
      <SectionHeader label="At a Glance" sub="For team leads · supplier base operational state" />

      {/* Total Suppliers — wide tile with dashed border */}
      <button
        type="button"
        onClick={drillAllSuppliers}
        className="w-full text-left bg-white border border-dashed border-line rounded-lg px-5 py-4 mb-4 flex items-center justify-between gap-6 hover:border-active-green/40 transition-colors">
        <div>
          <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#51534A] mb-1">Total Suppliers</p>
          <p className="text-2xl font-bold text-ink leading-none">{total}</p>
        </div>
        <p className="text-[11px] text-[#51534A] text-right">
          {addedLast30d} added in last 30 days · {onboarded} onboarded · {archivedThisYear} archived this year
        </p>
      </button>

      {/* Operational status row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
        <StatusTile color="#92D400" label="Active"        value={opBuckets.active.length}        meaning="Ready to brief — admins set this manually" onClick={() => drillOpStatus('active')} />
        <StatusTile color="#009159" label="Preferred"     value={opBuckets.preferred.length}     meaning="Go-to suppliers for top-tier work"        onClick={() => drillOpStatus('preferred')} />
        <StatusTile color="#E89A2E" label="On Hold"       value={opBuckets.on_hold.length}       meaning="Paused — see notes per supplier"          onClick={() => drillOpStatus('on_hold')} />
        <StatusTile color="#E0290A" label="Do Not Use"    value={opBuckets.do_not_use.length}    meaning="Blocked from new briefs"                  onClick={() => drillOpStatus('do_not_use')} mutedZero />
        <StatusTile color="#2E8FA1" label="Pending Set-up" value={opBuckets.pending_setup.length} meaning="Awaiting Fusion accounts"                onClick={() => drillOpStatus('pending_setup')} />
      </div>

      {/* Backlog insight pill */}
      {showBacklog && (
        <div className="bg-[#2E8FA1]/[0.06] border border-[#2E8FA1]/30 rounded-lg p-3.5 mb-4 flex items-center gap-3">
          <span className="text-[#2E8FA1] flex-shrink-0">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
          </span>
          <p className="flex-1 text-xs text-fusion-mid-grey leading-relaxed">
            <strong className="text-ink font-semibold">{opBuckets.pending_setup.length} suppliers</strong> still need Fusion accounts set up. This is your biggest operational backlog — most of your supplier base can't be invited to briefs yet.
          </p>
          <button
            type="button"
            onClick={() => navigate(suppliersListUrl({ opStatus: 'pending_setup', sort: 'created_asc' }))}
            className="text-[11px] font-bold tracking-[0.15em] uppercase text-[#2E8FA1] hover:text-[#3aa8bd] transition-colors whitespace-nowrap">
            Go to Setup Queue →
          </button>
        </div>
      )}

      {/* Engagement row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <EngagementCard
          label="Dormant Suppliers"
          value={dormantList.length}
          accentClass="text-[#7A4E10]"
          subline="no activity in 90+ days"
          context={`${(suppliers || []).filter(s => s.status === 'active').length} active total`}
          onClick={() => drillDormant(dormantTimeframe)} />
        <EngagementCard
          label="New Suppliers (30D)"
          value={newSuppliers30d.count}
          delta={newSuppliers30d.delta}
          subline="added in the last 30 days"
          context={`${onboarded} onboarded · ${total - onboarded} pending Fusion setup`}
          onClick={() => drillNewlyCreated('30d')} />
        <EngagementCard
          label="Under-utilised"
          value={underUtilisedCount}
          accentClass="text-ink"
          subline="active but not invited in 90+ days"
          context="Active suppliers worth re-engaging"
          onClick={drillUnderUtilised} />
      </div>

      {/* Top responding + Dormant detail */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-10">
        <Card>
          <CardHeader title="Top Responding Suppliers">
            <select
              value={topRespondingSort}
              onChange={(e) => setTopRespondingSort(e.target.value)}
              className="bg-white border border-line rounded text-fusion-mid-grey text-[10px] font-bold tracking-[0.15em] uppercase px-2 py-1 cursor-pointer">
              <option value="response_count">By Response Count</option>
              <option value="win_rate">By Win Rate</option>
            </select>
          </CardHeader>
          {topResponding.length === 0 ? (
            <EmptyState message="No supplier responses recorded in this period. Once briefs receive responses, suppliers will rank here by engagement." />
          ) : (
            <div className="overflow-x-auto">
              {/* Top responding: rank, name, two figures -- the name is truncate, so without a floor it truncates to nothing. */}
              <table className="w-full text-xs min-w-[420px]">
                <tbody>
                  {topResponding.map((s, i) => {
                    const rate = s.responded > 0 ? (s.won || 0) / s.responded : 0;
                    return (
                      <tr
                        key={s.id}
                        onClick={() => drillSupplier(s.id)}
                        className="border-t border-line hover:bg-active-green/[0.04] cursor-pointer transition-colors">
                        <td className="py-2 pr-2 w-6 text-[#51534A]">{i + 1}</td>
                        <td className="py-2 pr-2 text-ink font-semibold truncate">{s.supplier_name}</td>
                        <td className="py-2 pr-2 text-[#51534A] text-right whitespace-nowrap">{s.responded || 0} responses</td>
                        <td className="py-2 text-fusion-forest text-right whitespace-nowrap">{Math.round(rate * 100)}% win</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
        <Card>
          <CardHeader title="Dormant Suppliers Detail" />
          {dormantList.length === 0 ? (
            <EmptyState message="No dormant suppliers in the selected period." />
          ) : (
            <div className="space-y-1.5">
              {dormantList.slice(0, 6).map(s => {
                const days = s.last_activity_at
                  ? Math.floor((Date.now() - new Date(s.last_activity_at).getTime()) / 86400000)
                  : null;
                return (
                  <div
                    key={s.id}
                    className="flex items-center justify-between gap-3 py-1.5 px-2 -mx-2 rounded hover:bg-white cursor-pointer transition-colors"
                    onClick={() => drillSupplier(s.id)}>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-semibold text-ink truncate">{s.supplier_name}</p>
                      <p className="text-[10px] text-[#51534A] truncate">
                        {s.category || 'No category'} · Tier {s.tier || '—'} · {s.last_activity_at ? `last brief ${fmtDate(s.last_activity_at)}` : 'Never invited to a brief'}
                      </p>
                    </div>
                    <p className="text-[10px] font-bold text-[#7A4E10] whitespace-nowrap">
                      {days != null ? `${days}d dormant` : 'never'}
                    </p>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); /* eslint-disable-next-line no-alert */ alert('Compose check-in flow — coming soon'); }}
                      className="text-[10px] font-bold tracking-[0.15em] uppercase px-2 py-1 rounded border border-[#2E8FA1]/40 text-[#2E8FA1] hover:bg-[#2E8FA1]/10 transition-colors">
                      Send Check-in
                    </button>
                  </div>
                );
              })}
              {dormantList.length > 6 && (
                <button
                  type="button"
                  onClick={() => drillDormant(dormantTimeframe)}
                  className="text-[11px] font-bold tracking-[0.15em] uppercase text-ink-secondary hover:text-fusion-forest mt-2">
                  View all {dormantList.length} dormant suppliers →
                </button>
              )}
            </div>
          )}
        </Card>
      </div>

      {/* ===== SECTION: POST-EVENT FEEDBACK ===== */}
      <SectionHeader label="Post-event Feedback" sub="How awarded work went · completion first, then what the answers say" />

      {feedback.reached === 0 ? (
        <div className="mb-10">
          <Card>
            <EmptyState message="No awarded briefs have reached their feedback window yet. Feedback is requested a week after each event (or 30 days after award when there's no event date), for awards at or above the value threshold set in Admin." />
          </Card>
        </div>
      ) : (
        <>
          {/* Completion rate FIRST — everything below is a read of the answers
              we have, so this says how complete the picture is. */}
          <div className="bg-white border border-dashed border-line rounded-lg px-5 py-4 mb-4 flex items-center justify-between gap-6">
            <div>
              <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#51534A] mb-1">Feedback Completion</p>
              <p className="text-3xl font-bold text-ink leading-none">
                {feedback.rate == null ? '—' : `${Math.round(feedback.rate * 100)}%`}
              </p>
              <p className="text-[11px] text-[#51534A] mt-1.5">
                {feedback.complete} of {feedback.reached} awarded brief{feedback.reached === 1 ? '' : 's'} answered
              </p>
            </div>
            <div className="text-right">
              <p className={`text-2xl font-bold leading-none ${feedback.outstanding > 0 ? 'text-[#7A4E10]' : 'text-fusion-forest'}`}>{feedback.outstanding}</p>
              <p className="text-[11px] text-[#51534A] mt-1.5">still to give — due or lapsed</p>
            </div>
          </div>

          {/* Base performance — reads only from completed answers. */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <Card>
              <CardHeader title="On Time" />
              {feedback.onTime == null ? (
                <EmptyState message="No timing answers yet." />
              ) : (
                <>
                  <p className="text-3xl font-bold text-ink mb-1">{Math.round(feedback.onTime.pct * 100)}%</p>
                  <p className="text-[11px] text-[#51534A]">arrived on time · across {feedback.onTime.of} answered</p>
                </>
              )}
            </Card>
            <Card>
              <CardHeader title="Would Use Again" />
              {feedback.useAgain == null ? (
                <EmptyState message="No answers yet." />
              ) : (
                <>
                  <p className="text-3xl font-bold text-fusion-forest mb-1">{Math.round(feedback.useAgain.pct * 100)}%</p>
                  <p className="text-[11px] text-[#51534A]">a clear yes · across {feedback.useAgain.of} answered</p>
                </>
              )}
            </Card>
            <Card>
              <CardHeader title="Open Safety Incidents" />
              <p className={`text-3xl font-bold mb-1 ${feedback.incOpen > 0 ? 'text-[#E0290A]' : 'text-[#51534A]'}`}>{feedback.incOpen}</p>
              <p className="text-[11px] text-[#51534A]">
                {feedback.incOpen === 0 ? 'none open' : `${feedback.incReportable} reportable · ${feedback.incMinor} minor`}
              </p>
            </Card>
          </div>

          {/* Completion by owner + watchlist */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-10">
            <Card>
              <CardHeader title="Completion by Owner" />
              {feedback.owners.length === 0 ? (
                <EmptyState message="No owners assigned on awarded briefs." />
              ) : (
                <div className="overflow-x-auto">
                  {/* Owner workload: Owner, Given, Outstanding -- sits in a half-width card. */}
                  <table className="w-full text-xs min-w-[340px]">
                    <thead>
                      <tr className="text-[#51534A] text-[10px] font-bold tracking-[0.15em] uppercase border-b border-line">
                        <th className="py-2 pr-2 text-left">Owner</th>
                        <th className="py-2 pr-2 text-right">Given</th>
                        <th className="py-2 text-right">Outstanding</th>
                      </tr>
                    </thead>
                    <tbody>
                      {feedback.owners.map((o, i) => (
                        <tr key={i} className="border-t border-line">
                          <td className="py-2 pr-2 text-ink font-semibold truncate">{o.name}</td>
                          <td className="py-2 pr-2 text-right text-[#51534A] whitespace-nowrap">{o.complete} / {o.reached}</td>
                          <td className={`py-2 text-right font-bold whitespace-nowrap ${o.reached - o.complete > 0 ? 'text-[#7A4E10]' : 'text-fusion-forest'}`}>
                            {o.reached - o.complete}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
            <Card>
              <CardHeader title={`Needs a Look${feedback.watchlist.length > 0 ? ` · ${feedback.watchlist.length}` : ''}`} />
              {feedback.watchlist.length === 0 ? (
                <EmptyState message="No issues flagged in the feedback given so far — every answered brief went cleanly." />
              ) : (
                <div className="space-y-1.5">
                  {feedback.watchlist.slice(0, 6).map((r, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between gap-3 py-1.5 px-2 -mx-2 rounded hover:bg-white cursor-pointer transition-colors"
                      onClick={() => drillSupplier(r.brief.awarded_supplier_id)}>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold text-ink truncate">{r.supplierName}</p>
                        <p className="text-[10px] text-[#51534A] truncate">
                          {r.brief.brief_name}{r.feedback?.completed_at ? ` · ${fmtDate(r.feedback.completed_at)}` : ''}
                        </p>
                      </div>
                      <span className={`text-[10px] font-bold whitespace-nowrap ${r.outcome.tone === 'bad' ? 'text-[#E0290A]' : 'text-[#7A4E10]'}`}>
                        {r.outcome.text}
                      </span>
                    </div>
                  ))}
                  {feedback.watchlist.length > 6 && (
                    <p className="text-[10px] text-[#51534A] mt-2">+{feedback.watchlist.length - 6} more</p>
                  )}
                </div>
              )}
            </Card>
          </div>
        </>
      )}

      {/* ===== SECTION 2: TRENDS & INSIGHTS ===== */}
      <SectionHeader label="Trends & Insights" sub="For directors · base composition, spend and engagement patterns" />

      {/* Total Awarded Value + Average Win Rate */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <Card>
          <CardHeader title="Total Awarded Value">
            <PeriodSelect value={spendTimeframe} onChange={setSpendTimeframe} />
          </CardHeader>
          <button
            type="button"
            onClick={drillAwardedTotal}
            className="w-full text-left">
            <p className="text-3xl font-bold text-fusion-forest mb-1">{fmtGbpFull(totalAwardedInTimeframe)}</p>
            <p className="text-[11px] text-[#51534A]">ex-VAT · across {awardedBriefs.length} awarded brief{awardedBriefs.length === 1 ? '' : 's'} in this window</p>
          </button>
          <div className="flex items-end gap-1 h-16 mt-4">
            {weeklySpend.map((w, i) => (
              <button
                key={i}
                type="button"
                onClick={() => navigate(briefsListUrl({ awardState: 'awarded', awardedWeek: w.key }))}
                title={`${w.label}: ${fmtGbp(w.total || 0)}`}
                className="flex-1 bg-[#92D400]/30 hover:bg-active-green/60 transition-colors rounded-sm"
                style={{ height: `${Math.max(2, ((w.total || 0) / weeklySpendMax) * 100)}%` }} />
            ))}
          </div>
        </Card>
        <Card>
          <CardHeader title="Average Win Rate" />
          {avgWinRate == null ? (
            <EmptyState
              title="—"
              message="Not enough data to calculate · min 3 quoted briefs per supplier. Once at least 3 suppliers cross the threshold, the rolling average lands here." />
          ) : (
            <button
              type="button"
              onClick={() => /* eslint-disable-next-line no-alert */ alert('Win-rate distribution modal — coming soon')}
              className="w-full text-left">
              <p className="text-3xl font-bold text-ink mb-1">{Math.round(avgWinRate * 100)}%</p>
              <p className="text-[11px] text-[#51534A]">across suppliers with ≥3 quoted briefs</p>
            </button>
          )}
        </Card>
      </div>

      {/* Awarded Value by Supplier + Tier Composition */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <Card>
          <CardHeader title="Awarded Value by Supplier">
            <PeriodSelect value={awardedBySupplierTf} onChange={setAwardedBySupplierTf} />
          </CardHeader>
          {awardedBySupplier.length === 0 ? (
            <EmptyState message="No awards recorded in this window." />
          ) : (
            <div className="space-y-2">
              {awardedBySupplier.map(s => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => drillAwardedToSupplier(s.id)}
                  className="w-full grid grid-cols-[120px_1fr_auto] gap-3 items-center text-xs text-left hover:bg-active-green/[0.04] -mx-2 px-2 py-1 rounded transition-colors">
                  <span className="text-ink truncate">{s.supplier_name}</span>
                  <div className="h-2 bg-white rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#92D400]"
                      style={{ width: `${(s.amount / awardedBySupplierMax) * 100}%` }} />
                  </div>
                  <span className="text-fusion-mid-grey font-semibold whitespace-nowrap">{fmtGbp(s.amount)} · {s.count}</span>
                </button>
              ))}
            </div>
          )}
        </Card>
        <Card>
          <CardHeader title="Tier Composition" />
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => drillTier(1)}
              className="bg-white border border-line rounded p-3 text-left hover:border-active-green/40 transition-colors">
              <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#51534A] mb-1">Tier 1</p>
              <p className="text-2xl font-bold text-ink">{tierComposition[1]}</p>
              <p className="text-[10px] text-[#51534A] mt-1">Strategic suppliers</p>
            </button>
            <button
              type="button"
              onClick={() => drillTier(2)}
              className="bg-white border border-line rounded p-3 text-left hover:border-active-green/40 transition-colors">
              <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-fusion-mid-grey mb-1">Tier 2</p>
              <p className="text-2xl font-bold text-ink">{tierComposition[2]}</p>
              <p className="text-[10px] text-[#51534A] mt-1">Standard suppliers</p>
            </button>
          </div>
          {tierComposition.unassigned > 0 && (
            <p className="text-[10px] text-[#51534A] mt-2">
              {tierComposition.unassigned} not yet tiered
            </p>
          )}
          {tierUnbalanced && (
            <div className="mt-3 bg-[#E89A2E]/10 border border-[#E89A2E]/30 rounded px-3 py-2 text-[11px] text-[#7A4E10]">
              ⚠ Only {tierComposition[1]} Tier 1 supplier{tierComposition[1] === 1 ? '' : 's'} — consider promoting top performers.
            </div>
          )}
        </Card>
      </div>

      {/* Suppliers by Category + Geographic Distribution */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <Card>
          <CardHeader title="Suppliers by Category" />
          {topCategories.length === 0 ? (
            <EmptyState message="No categorised suppliers yet." />
          ) : (
            <div className="space-y-2">
              {topCategories.map(c => (
                <button
                  key={c.name}
                  type="button"
                  onClick={() => drillCategory(c.name)}
                  className="w-full grid grid-cols-[120px_1fr_auto] gap-3 items-center text-xs text-left hover:bg-active-green/[0.04] -mx-2 px-2 py-1 rounded transition-colors">
                  <span className="text-ink truncate">{c.name}</span>
                  <div className="h-2 bg-white rounded-full overflow-hidden">
                    <div className="h-full bg-[#92D400]" style={{ width: `${(c.count / categoryMax) * 100}%` }} />
                  </div>
                  <span className="text-fusion-mid-grey font-semibold whitespace-nowrap">{c.count} · {c.pct.toFixed(0)}%</span>
                </button>
              ))}
              {totalCategoryCount > 7 && (
                <button
                  type="button"
                  onClick={() => /* eslint-disable-next-line no-alert */ alert('All categories view — coming soon')}
                  className="text-[11px] font-bold tracking-[0.15em] uppercase text-ink-secondary hover:text-fusion-forest mt-2">
                  View all {totalCategoryCount} categories →
                </button>
              )}
            </div>
          )}
        </Card>
        <Card>
          <CardHeader title="Geographic Distribution" />
          {byRegion.length === 0 ? (
            <EmptyState message="No region data on file." />
          ) : (
            <>
              <div className="space-y-2">
                {byRegion.slice(0, 6).map((r, i) => (
                  <button
                    key={r.name}
                    type="button"
                    onClick={() => drillRegion(r.name)}
                    className="w-full grid grid-cols-[120px_1fr_auto] gap-3 items-center text-xs text-left hover:bg-active-green/[0.04] -mx-2 px-2 py-1 rounded transition-colors">
                    <span className="text-ink truncate">{r.name}</span>
                    <div className="h-2 bg-white rounded-full overflow-hidden">
                      <div
                        className="h-full"
                        style={{ width: `${(r.count / regionMax) * 100}%`, background: i === 0 ? '#92D400' : '#2E8FA1' }} />
                    </div>
                    <span className="text-fusion-mid-grey font-semibold whitespace-nowrap">{r.count} · {r.pct.toFixed(0)}%</span>
                  </button>
                ))}
              </div>
              {regionsConcentrated && topRegion && (
                <div className="mt-3 bg-[#E89A2E]/10 border border-[#E89A2E]/30 rounded px-3 py-2 text-[11px] text-[#7A4E10]">
                  ⚠ {topRegion.name} holds {Math.round(topRegion.pct)}% of the supplier base — consider regional diversity.
                </div>
              )}
            </>
          )}
        </Card>
      </div>

      {/* Most Engaged Suppliers — wide */}
      <Card>
        <CardHeader title="Most Engaged Suppliers" />
        {mostEngaged.length === 0 ? (
          <EmptyState message="Engagement data builds over time. Once suppliers have been invited to multiple briefs, their response patterns will rank here." />
        ) : (
          <div className="overflow-x-auto">
            {/* Most engaged: rank, supplier, three figures. */}
            <table className="w-full text-xs min-w-[480px]">
              <thead>
                <tr className="text-[#51534A] text-[10px] font-bold tracking-[0.15em] uppercase border-b border-line">
                  <th className="py-2 pr-2 text-left w-6">#</th>
                  <th className="py-2 pr-2 text-left">Supplier</th>
                  <th className="py-2 pr-2 text-right">Invited</th>
                  <th className="py-2 pr-2 text-right">Submitted</th>
                  <th className="py-2 text-right">Response rate</th>
                </tr>
              </thead>
              <tbody>
                {mostEngaged.map((s, i) => (
                  <tr
                    key={s.id}
                    onClick={() => drillSupplier(s.id)}
                    className="border-t border-line hover:bg-active-green/[0.04] cursor-pointer transition-colors">
                    <td className="py-2 pr-2 text-[#51534A]">{i + 1}</td>
                    <td className="py-2 pr-2">
                      <p className="text-ink font-semibold">{s.supplier_name}</p>
                      <p className="text-[10px] text-[#51534A]">{s.category || 'Uncategorised'} · Tier {s.tier || '—'}</p>
                    </td>
                    <td className="py-2 pr-2 text-right text-fusion-mid-grey">{s.invited}</td>
                    <td className="py-2 pr-2 text-right text-fusion-mid-grey">{s.responded}</td>
                    <td className="py-2 text-right text-fusion-forest font-bold">{Math.round(s.rate * 100)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

// ============================ subcomponents ============================

function Card({ children }) {
  return (
    <div className={CARD_SHELL_SM}>
      {children}
    </div>
  );
}

function CardHeader({ title, children }) {
  return (
    <div className="flex items-center justify-between gap-3 mb-3">
      <p className="text-[10px] font-bold tracking-[0.25em] uppercase text-[#51534A]">{title}</p>
      {children}
    </div>
  );
}

function EmptyState({ title, message }) {
  return (
    <div className="text-center py-6">
      {title && <p className="text-3xl font-bold text-[#51534A] mb-2">{title}</p>}
      <p className="text-[11px] text-[#51534A] leading-relaxed max-w-sm mx-auto">{message}</p>
    </div>
  );
}

function StatusTile({ color, label, value, meaning, onClick, mutedZero }) {
  const numColor = mutedZero && value === 0 ? '#51534A' : color;
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left bg-white border border-line rounded-lg p-3 hover:border-[#DADCCD] transition-colors"
      style={{ borderLeftWidth: 3, borderLeftColor: color }}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
        <p className="text-[9px] font-bold tracking-[0.2em] uppercase text-[#51534A]">{label}</p>
      </div>
      <p className="text-3xl font-bold leading-none" style={{ color: numColor }}>{value}</p>
      <p className="text-[10px] text-[#51534A] mt-2 leading-snug">{meaning}</p>
    </button>
  );
}

function EngagementCard({ label, value, accentClass = 'text-ink', subline, context, delta, timeframe, onTimeframeChange, onClick }) {
  return (
    <div className={CARD_SHELL_SM}>
      <div className="flex items-center justify-between gap-3 mb-1">
        <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#51534A]">{label}</p>
        {timeframe && onTimeframeChange && <PeriodSelect value={timeframe} onChange={onTimeframeChange} />}
      </div>
      <button type="button" onClick={onClick} className="w-full text-left">
        <p className={`text-3xl font-bold leading-none ${accentClass}`}>{value}</p>
        <div className="flex items-baseline gap-2 mt-1">
          <p className="text-[11px] text-[#51534A]">{subline}</p>
          {delta != null && (
            <span className={`text-[10px] font-bold ${delta > 0 ? 'text-fusion-forest' : delta < 0 ? 'text-[#E0290A]' : 'text-[#51534A]'}`}>
              {delta > 0 ? '▲' : delta < 0 ? '▼' : ''} {Math.abs(delta)} vs prev
            </span>
          )}
        </div>
        <p className="text-[10px] text-[#51534A] mt-2 leading-snug">{context}</p>
      </button>
    </div>
  );
}

function PeriodSelect({ value, onChange }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="bg-white border border-line rounded text-fusion-mid-grey text-[10px] font-bold tracking-[0.15em] uppercase px-2 py-0.5 cursor-pointer">
      {TIMEFRAMES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
    </select>
  );
}
